import PropTypes from 'prop-types';
import { PERMISSIONS } from '../../constants';
import { useContext, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import {
  deleteExpense,
  getExpense,
  getExpenses,
} from '../../utils/API/expenses';
import { UserContext } from '../../context/UserContext';
import { ExpenseForm } from './ExpenseForm';
import I3Modal from '../../components/Layout/I3Modal';
import I3ModalConfirmation from '../../components/Layout/I3ModalConfirmation';
import { Unauthorized } from '../../components/Unauthorized';
import { currencyFormatter } from '../../components/Table/utility';
import { renderSpinner } from '../../utils/Utility';
import ExpensesDataGrid from './ExpensesDataGrid';

const remappingCost = (expense) => ({
  ...expense,
  raw_cost: expense.cost,
  cost: expense.real_cost,
});

const fetchData = async (setExpenses, date, selectedUser) => {
  const expensesData = await getExpenses(date, selectedUser);
  const orderedData = _.orderBy(expensesData, ['date'], ['asc']);
  setExpenses(orderedData.map(remappingCost));
};

const Expenses = ({
  showModal,
  handleShowModal,
  handleHideModal,
  showModalTableConfirm,
  handleShowModalTableConfirm,
  handleHideModalTableConfirm,
  month,
  isReportConfirmed,
  isReadOnly,
  isAdminViewingOther,
  selectedUser,
}) => {
  const { user, hasClaim } = useContext(UserContext);
  const selectedUserFinal = selectedUser ?? user.username;
  const [expenses, setExpenses] = useState([]);
  const [currentData, setCurrentData] = useState(false);
  const [isDeleteRunning, setIsDeleteRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const isMountedRef = useRef(true);
  const updateData = () => {
    if (!isMountedRef.current) {
      return Promise.resolve();
    }
    setIsLoading(true);
    const setExpensesSafe = (next) => {
      if (isMountedRef.current) {
        setExpenses(next);
      }
    };
    return fetchData(setExpensesSafe, month, selectedUserFinal).finally(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    });
  };

  const handleHideTripModal = () => {
    handleHideModal();
    setCurrentData(false);
  };

  useEffect(() => {
    updateData();
  }, [month, selectedUserFinal]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const edit = (id) => {
    const cachedExpense = expenses.find((expense) => expense.id === id);
    if (cachedExpense) {
      setCurrentData({
        ...cachedExpense,
        cost: cachedExpense.raw_cost ?? cachedExpense.cost,
      });
      handleShowModal();
      return;
    }

    setIsEditLoading(true);
    getExpense(id, selectedUserFinal)
      .then((trip) => {
        if (!isMountedRef.current) {
          return trip;
        }
        setCurrentData(trip);
        handleShowModal();
        return trip;
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsEditLoading(false);
        }
      });
  };

  const remove = (id) => {
    handleShowModalTableConfirm(id);
    setCurrentData(id);
  };

  const onConfirm = () => {
    setIsDeleteRunning(true);
    deleteExpense(currentData)
      .then(updateData)
      .finally(() => {
        setIsDeleteRunning(false);
        handleHideModalTableConfirm();
      });
  };

  const expenseSummary = () => {
    const item = expenses.find((e) => e.id === currentData);
    if (!item) return '';
    const { date, description, real_cost: cost } = item;
    return `${date} - ${description} - ${currencyFormatter.format(cost)}`;
  };

  let modalTitle = 'Inserisci una nuova spesa';
  if (currentData?.id) {
    modalTitle = isReadOnly ? 'Visualizza spesa' : 'Modifica spesa';
  }

  const canView = hasClaim(PERMISSIONS.TRIPS_EXPENSES_VIEW) || hasClaim(PERMISSIONS.TRIPS_EXPENSES_USER_SELECTION);
  const canEdit = hasClaim(PERMISSIONS.TRIPS_EXPENSES_EDIT);
  const isReadOnlyEffective = isReadOnly || !canEdit;

  return canView ? (
    <>
      {renderSpinner(isEditLoading)}
      {isLoading ? (
        renderSpinner(true)
      ) : (
        <ExpensesDataGrid
          data={expenses}
          onEditClick={edit}
          onDeleteClick={canEdit ? remove : undefined}
          isReportConfirmed={isReportConfirmed || !canEdit}
          isAdminViewingOther={isAdminViewingOther || !canEdit}
        />
      )}
      <I3ModalConfirmation
        show={showModalTableConfirm.expense}
        onHide={handleHideModalTableConfirm}
        title="Cancella nota spese"
        questionTitle="Vuoi cancellare la spesa?"
        questionText={expenseSummary()}
        onConfirm={onConfirm}
        isLoading={isDeleteRunning}
      />

      <I3Modal show={showModal} onHide={handleHideTripModal} title={modalTitle}>
        <ExpenseForm
          close={handleHideTripModal}
          currentData={currentData}
          updateData={updateData}
          month={month}
          isReadOnly={isReadOnlyEffective}
        />
      </I3Modal>
    </>
  ) : (
    <Unauthorized />
  );
};

Expenses.defaultProps = {
  showModal: false,
  handleShowModal: () => {},
  handleHideModal: () => {},
  showModalTableConfirm: false,
  handleShowModalTableConfirm: () => {},
  handleHideModalTableConfirm: () => {},
  month: moment(),
  selectedUser: null,
  isReportConfirmed: false,
  isReadOnly: false,
  isAdminViewingOther: false,
};

Expenses.propTypes = {
  showModal: PropTypes.bool,
  handleShowModal: PropTypes.func,
  handleHideModal: PropTypes.func,
  showModalTableConfirm: PropTypes.shape({
    trip: PropTypes.bool,
    expense: PropTypes.bool,
  }),
  handleShowModalTableConfirm: PropTypes.func,
  handleHideModalTableConfirm: PropTypes.func,
  month: PropTypes.instanceOf(moment),
  selectedUser: PropTypes.string,
  isReportConfirmed: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isAdminViewingOther: PropTypes.bool,
};

export default Expenses;
